
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const formSchema = z.object({
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein."),
  subject: z.string().min(5, "Betreff muss mindestens 5 Zeichen lang sein."),
  message: z.string().min(20, "Nachricht muss mindestens 20 Zeichen lang sein."),
});

type FormValues = z.infer<typeof formSchema>;

const CreditRefundForm: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: user?.email || "",
      subject: "Credit-Rückerstattung anfragen",
      message: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Send the email using the send-email edge function
      const response = await fetch(
        "https://qmuqbkpcfslysjbptlnj.supabase.co/functions/v1/send-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            to: "support@vibemail.ch", // Replace with your support email
            subject: `[Credit-Anfrage] ${data.subject}`,
            html: `
              <h2>Credit-Rückerstattungsanfrage</h2>
              <p><strong>Von:</strong> ${data.email}</p>
              <p><strong>Betreff:</strong> ${data.subject}</p>
              <p><strong>Nachricht:</strong></p>
              <p>${data.message.replace(/\n/g, "<br>")}</p>
              ${user ? `<p><strong>Benutzer-ID:</strong> ${user.id}</p>` : ""}
            `,
            from: "VibeMail <support@vibemail.ch>",
            replyTo: data.email,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Fehler beim Senden der Anfrage");
      }

      toast({
        title: "Anfrage gesendet",
        description: "Wir werden Ihre Anfrage so schnell wie möglich bearbeiten.",
        duration: 5000,
      });
      
      form.reset({
        email: user?.email || "",
        subject: "Credit-Rückerstattung anfragen",
        message: "",
      });
    } catch (error: any) {
      console.error("Error sending credit refund request:", error);
      toast({
        title: "Fehler",
        description: error.message || "Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-Mail</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Ihre E-Mail-Adresse"
                  className="bg-gray-700 border-gray-600 text-white"
                  disabled={!!user?.email}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Betreff</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Betreff Ihrer Anfrage"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nachricht</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Beschreiben Sie Ihr Problem mit den Credits und geben Sie an, warum Sie eine Rückerstattung beantragen"
                  className="h-32 bg-gray-700 border-gray-600 text-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-jade hover:bg-jade/90"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Wird gesendet...
            </>
          ) : (
            "Anfrage senden"
          )}
        </Button>
      </form>
    </Form>
  );
};

export default CreditRefundForm;
